import {
  Box,
  Button,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Code,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiX,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import PartySelectById from "./PartySelectById";
import AccountSelectById from "./AccountSelectById";
import { debounce } from "lodash";

const SmartLedgersEditor = ({ smartLedger, onClose, isNew }) => {
  const [smartLedgerHeader, setSmartLedgerHeader] = useState({});
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (smartLedger) {
      setSmartLedgerHeader({ ...smartLedger, accounts: undefined });
      setAccounts(smartLedger.accounts || []);
    }
  }, [smartLedger]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setSmartLedgerHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 5),
    []
  );

  const handleSave = async () => {
    try {
      const fullSmartLedger = { ...smartLedgerHeader, accounts };

      console.log(
        "Saving Smart Ledger:",
        JSON.stringify(fullSmartLedger, null, 2)
      );
      const response = await fetch(
        isNew
          ? "https://ledgers.poc.mrc.ai/api/smartledgers"
          : `https://ledgers.poc.mrc.ai/api/smartledgers/${smartLedgerHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullSmartLedger, null, 2),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} smartLedger. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Smart Ledger created" : "Smart Ledger saved",
        description: isNew
          ? "New Smart Ledger was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/smartledgers/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving Smart Ledger:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving Smart Ledger.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  // Helper to handle account value change
  const handleAccountChange = (index, value) => {
    setAccounts((prev) =>
      prev.map((acc, i) => (i === index ? { id: value.id } : acc))
    );
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Smart Ledger</Heading>
                <Tooltip
                  label="Save Smart Ledger"
                  aria-label="Save Smart Ledger Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Smart Ledger Details</Tab>
              <Tab>Accounts</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={smartLedgerHeader._id} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={smartLedgerHeader.name || ""}
                        onChange={(e) =>
                          handleHeaderChange("name", e.target.value)
                        }
                      />
                    </FormControl>

                    <CodesetSelect
                      label="Type"
                      codesetCode="SLC001"
                      value={
                        smartLedgerHeader.type
                          ? { code: smartLedgerHeader.type, description: "" }
                          : null
                      }
                      onChange={(val) => handleHeaderChange("type", val?.code)}
                    />

                    <PartySelectById
                      label="Owner"
                      selectedPartyId={smartLedgerHeader.owner}
                      onChange={(value) => handleHeaderChange("owner", value)}
                      role=""
                      size="md"
                    />
                  </SimpleGrid>

                  <SimpleGrid
                    columns={{ base: 1, md: 5 }}
                    spacing={6}
                    mt={4}></SimpleGrid>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack alignItems="center" mb={4}>
                    <Heading size="md">Accounts</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="md"
                      onClick={() => setAccounts([...accounts, { id: "" }])}
                      aria-label="Add Account"
                    />
                  </HStack>
                  <Box overflowX="auto">
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px" }}>
                            Account
                          </th>
                          <th style={{ width: "50px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map((account, index) => (
                          <tr key={index}>
                            <td style={{ padding: "8px" }}>
                              <AccountSelectById
                                label=""
                                selectedAccountId={account.id}
                                onChange={(value) =>
                                  handleAccountChange(index, value)
                                }
                                size="md"
                              />
                            </td>
                            <td style={{ padding: "8px" }}>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="sm"
                                onClick={() => {
                                  const newAccounts = accounts.filter(
                                    (_, i) => i !== index
                                  );
                                  setAccounts(newAccounts);
                                }}
                                aria-label="Remove Account"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <VStack spacing={6} align="stretch"></VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default SmartLedgersEditor;
